import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  alpha,
  Avatar,
  Box,
  Chip,
  Collapse,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { type FC, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { sortPendingActivityEvents } from './sortPendingActivityEvents.js';
import {
  type ActivityEvent,
  useReviewActivityEvent,
} from '../../api/activity.api.js';
import { type Patient } from '../../models/index.js';
import { activityEventRemoved } from '../../redux/reducers/pendingActivityEvents.js';
import { getPendingActivityEvents } from '../../redux/selectors.js';
import ActivityEventInlineClip from '../Activity/ActivityEventInlineClip.js';
import ActivityRejectDialog from '../Activity/ActivityRejectDialog.js';
import ActivityReviewPriorityMarker from '../Activity/ActivityReviewPriorityMarker.js';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Patients currently rendered on the streams grid — used for the
   * "My patients" / "All visible" filter. */
  visiblePatients: Patient[];
}

function initials(p: Patient): string {
  return `${p.firstName?.[0] ?? ''}${p.lastName?.[0] ?? ''}`.toUpperCase();
}

function timeAgo(iso: string): string {
  const diff = DateTime.fromISO(iso).diffNow(['minutes', 'seconds']);
  const mins = Math.abs(Math.floor(diff.minutes));
  if (mins >= 1) return `${mins} min ago`;
  return `${Math.max(Math.abs(Math.floor(diff.seconds)), 1)} sec ago`;
}

const ActivityReviewDrawer: FC<Props> = ({ open, onClose, visiblePatients }) => {
  const dispatch = useDispatch();
  const pending = useSelector(getPendingActivityEvents);

  const [scope, setScope] = useState<'visible' | 'all'>('visible');
  const [openClipId, setOpenClipId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<ActivityEvent | null>(null);

  const patientById = useMemo(() => {
    const m = new Map<string, Patient>();
    visiblePatients.forEach((p) => m.set(p.id, p));
    return m;
  }, [visiblePatients]);

  const items = useMemo(() => {
    const inScope = scope === 'visible' ? pending.filter((e) => patientById.has(e.patientId)) : pending;
    return sortPendingActivityEvents(inScope);
  }, [pending, scope, patientById]);
  const highPriorityCount = items.filter((e) => e.reviewPriority === 'high').length;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 360 } }}
      sx={{ zIndex: 1300 }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
      >
        <AutoAwesomeIcon fontSize="small" sx={{ color: 'primary.main' }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ lineHeight: 1.2 }}>
            AI suggestions
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {items.length} pending
            {highPriorityCount > 0 && (
              <Box component="span" sx={{ color: 'warning.main' }}> · {highPriorityCount} high priority</Box>
            )}
            {' · last 30 min'}
          </Typography>
        </Box>
        <IconButton edge="end" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ px: 2, py: 1 }}>
        <Chip
          size="small"
          label="My patients"
          color={scope === 'visible' ? 'primary' : 'default'}
          variant={scope === 'visible' ? 'filled' : 'outlined'}
          onClick={() => setScope('visible')}
        />
        <Chip
          size="small"
          label="All visible"
          color={scope === 'all' ? 'primary' : 'default'}
          variant={scope === 'all' ? 'filled' : 'outlined'}
          onClick={() => setScope('all')}
        />
      </Stack>

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {items.length === 0 ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={1}
            sx={{ py: 8, px: 3, textAlign: 'center' }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 40, opacity: 0.3 }} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No AI events to review
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              Suggestions from the last 30 minutes appear here.
            </Typography>
          </Stack>
        ) : (
          items.map((event) => (
            <Row
              key={event.id}
              event={event}
              patient={patientById.get(event.patientId)}
              clipOpen={openClipId === event.id}
              onToggleClip={() =>
                setOpenClipId((curr) => (curr === event.id ? null : event.id))
              }
              onAccept={() => dispatch(activityEventRemoved(event.id))}
              onReject={() => setRejecting(event)}
            />
          ))
        )}
      </Box>

      {rejecting && (
        <ActivityRejectDialog
          event={rejecting}
          onClose={() => setRejecting(null)}
          onReviewed={(reviewed) => {
            dispatch(activityEventRemoved(reviewed.id));
            setRejecting(null);
          }}
        />
      )}
    </Drawer>
  );
};

interface RowProps {
  event: ActivityEvent;
  patient?: Patient;
  clipOpen: boolean;
  onToggleClip: () => void;
  onAccept: () => void;
  onReject: () => void;
}

const Row: FC<RowProps> = ({ event, patient, clipOpen, onToggleClip, onAccept, onReject }) => {
  const review = useReviewActivityEvent(event.id);
  const categoryColor = event.code.category?.color;

  const handleAccept = () => {
    onAccept();
    review.mutate({ verdict: 'accepted' });
  };

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: 1,
        borderColor: 'divider',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Avatar
          sx={{
            width: 28,
            height: 28,
            fontSize: 12,
            bgcolor: 'action.selected',
            color: 'text.primary',
          }}
        >
          {patient ? initials(patient) : '·'}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="baseline" spacing={0.75}>
            <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
              {patient ? `${patient.firstName} ${patient.lastName}` : 'Unknown patient'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              · {event.camera.name}
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
            {timeAgo(event.capturedAt)}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.5 }}>
            <Chip
              size="small"
              label={event.code.code}
              sx={{
                fontWeight: 600,
                bgcolor: categoryColor
                  ? alpha(categoryColor, 0.15)
                  : (t) => alpha(t.palette.primary.main, 0.1),
                color: 'text.primary',
                height: 20,
              }}
            />
            <Typography variant="body2" sx={{ color: 'text.primary' }} noWrap>
              {event.code.description}
            </Typography>
            <ActivityReviewPriorityMarker priority={event.reviewPriority} />
          </Stack>
        </Box>
        <Stack direction="row" spacing={0.25}>
          <Tooltip title="Preview clip">
            <IconButton size="small" onClick={onToggleClip}>
              <PlayArrowIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Accept">
            <IconButton
              size="small"
              color="success"
              onClick={handleAccept}
              disabled={review.isLoading}
            >
              <CheckIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reject — pick a different code">
            <IconButton
              size="small"
              color="error"
              onClick={onReject}
              disabled={review.isLoading}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      <Collapse in={clipOpen} unmountOnExit>
        <Box sx={{ ml: 4.5, mt: 1, height: 160, display: 'flex' }}>
          <ActivityEventInlineClip eventId={event.id} open={clipOpen} />
        </Box>
      </Collapse>
    </Box>
  );
};

export default ActivityReviewDrawer;
